import { NavLink, useLocation } from "react-router-dom";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { motion } from "framer-motion";
import { Logo } from "../Logo";
import { useLanguage } from "../LanguageProvider";
import { useAuth } from "../AuthProvider";

export const Sidebar = ({ collapsed, onToggle, onMobileClose }) => {
  const location = useLocation();
  const { t } = useLanguage();
  const { user, logout } = useAuth();

  const navigationItems = [
    { titleKey: "dashboard", icon: "fa-solid fa-chart-line", path: "/" },
    { titleKey: "search", icon: "fa-solid fa-magnifying-glass", path: "/search" },
    { titleKey: "ontologyBuilder", icon: "fa-solid fa-diagram-project", path: "/ontology" },
    { titleKey: "graphExplorer", icon: "fa-solid fa-circle-nodes", path: "/graph" },
    { titleKey: "gisMapView", icon: "fa-solid fa-map-location-dot", path: "/map" },
    { titleKey: "timelineAnalytics", icon: "fa-solid fa-timeline", path: "/timeline" },
    { titleKey: "worksheetBuilder", icon: "fa-solid fa-table-cells-large", path: "/worksheet" },
    { titleKey: "alertsReports", icon: "fa-solid fa-bell", path: "/alerts" },
    { titleKey: "adminSettings", icon: "fa-solid fa-gear", path: "/admin" },
    { titleKey: "designSystem", icon: "fa-solid fa-palette", path: "/design-system" },
    { titleKey: "downloads", icon: "fa-solid fa-download", path: "/downloads" },
  ];

  // Close mobile menu when navigating
  const handleNavClick = () => {
    if (window.innerWidth < 1024 && onMobileClose) {
      onMobileClose();
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    if (onMobileClose) onMobileClose();
    await logout();
  };

  // Get user display info
  const displayName = user?.username || "Guest";
  const initials = displayName.substring(0, 2).toUpperCase();

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 72 : 260 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      className="flex flex-col bg-sidebar-bg border-r border-sidebar-muted h-full"
    >
      {/* Logo Section */}
      <div className="flex h-14 lg:h-16 items-center justify-between px-3 lg:px-4 border-b border-sidebar-muted">
        {/* Desktop: Show Logo component */}
        <div className="hidden lg:block">
          <Logo collapsed={collapsed} />
        </div>
        
        {/* Mobile: Always show full logo with name */}
        <div className="flex lg:hidden items-center gap-3">
          <Logo collapsed={false} />
        </div>
        
        {/* Desktop toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="h-8 w-8 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-muted hidden lg:flex"
        >
          <i className={cn(
            "fa-solid text-sm transition-transform duration-200",
            collapsed ? "fa-angles-right" : "fa-angles-left"
          )}></i>
        </Button>

        {/* Mobile close */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMobileClose}
          className="h-8 w-8 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-muted lg:hidden"
        >
          <i className="fa-solid fa-xmark text-sm"></i>
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-3 lg:py-4">
        <TooltipProvider delayDuration={0}>
          <nav className="flex flex-col gap-1 px-2 lg:px-3">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path;
              const title = t(item.titleKey);
              
              const linkContent = (
                <NavLink
                  to={item.path}
                  onClick={handleNavClick}
                  className={cn(
                    "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                    "hover:bg-sidebar-muted",
                    isActive 
                      ? "bg-sidebar-active-bg text-sidebar-active-text" 
                      : "text-sidebar-foreground/70 hover:text-sidebar-foreground"
                  )}
                >
                  <div className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors",
                    isActive 
                      ? "bg-sidebar-accent/30 text-sidebar-active-text" 
                      : "text-sidebar-foreground/60 group-hover:text-sidebar-foreground"
                  )}>
                    <i className={cn(item.icon, "text-base")}></i>
                  </div>
                  
                  <motion.span
                    initial={false}
                    animate={{ 
                      opacity: collapsed ? 0 : 1, 
                      width: collapsed ? 0 : "auto" 
                    }}
                    className="whitespace-nowrap overflow-hidden"
                  >
                    {title}
                  </motion.span>
                  
                  {isActive && !collapsed && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="ml-auto h-1.5 w-1.5 rounded-full bg-sidebar-accent"
                    />
                  )}
                </NavLink>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {title}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return <div key={item.path}>{linkContent}</div>;
            })}
          </nav>
        </TooltipProvider>
      </ScrollArea>

      {/* Bottom Section - User Profile & Sign Out */}
      <div className="border-t border-sidebar-muted p-2 lg:p-3">
        <TooltipProvider delayDuration={0}>
          <div className={cn(
            "flex items-center gap-3 rounded-lg px-2 lg:px-3 py-2",
            collapsed ? "justify-center" : ""
          )}>
            <div className="relative">
              <div className="flex h-8 w-8 lg:h-9 lg:w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-glow">
                <span className="text-xs lg:text-sm font-semibold text-primary-foreground">{initials}</span>
              </div>
              <span className="absolute bottom-0 right-0 h-2 w-2 lg:h-2.5 lg:w-2.5 rounded-full bg-success border-2 border-sidebar-bg"></span>
            </div>
            
            <motion.div
              initial={false}
              animate={{ 
                opacity: collapsed ? 0 : 1, 
                width: collapsed ? 0 : "auto" 
              }}
              className="flex-1 overflow-hidden"
            >
              <p className="text-sm font-medium text-sidebar-foreground truncate capitalize">{displayName}</p>
              <p className="text-xs text-sidebar-foreground/60 truncate">{t("authenticated") || "Authenticated"}</p>
            </motion.div>

            {!collapsed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 lg:h-8 lg:w-8 text-sidebar-foreground/60 hover:text-destructive hover:bg-destructive/10"
                    onClick={handleSignOut}
                    data-testid="sign-out-button"
                  >
                    <i className="fa-solid fa-right-from-bracket text-xs lg:text-sm"></i>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">{t("signOut") || "Sign out"}</TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Sign out button when collapsed */}
          {collapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-full h-8 mt-2 text-sidebar-foreground/60 hover:text-destructive hover:bg-destructive/10"
                  onClick={handleSignOut}
                  data-testid="sign-out-button-collapsed"
                >
                  <i className="fa-solid fa-right-from-bracket text-sm"></i>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{t("signOut") || "Sign out"}</TooltipContent>
            </Tooltip>
          )}
        </TooltipProvider>
      </div>
    </motion.aside>
  );
};
